/**
 * Domain status unions and their colour/label maps, kept apart from the
 * component so `status-badge.tsx` only exports a component (fast refresh)
 * and callers can validate their data against the same maps the badge
 * renders from.
 */
export type CustomerBadgeStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
export type ContractBadgeStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'EXPIRED' | 'TERMINATED';
export type MenuBadgeStatus = 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type CalendarBadgeStatus = 'PUBLISHED' | 'DRAFT';
export type HolidayBadgeStatus = 'HAS_SERVICE' | 'NO_SERVICE';
export type OrderBadgeStatus = 'DRAFT' | 'SUBMITTED' | 'CONFIRMED' | 'CANCELLED';
export type ProductionBadgeStatus = 'PROVISIONAL' | 'CLOSED';
export type DeliveryNoteBadgeStatus =
  'PENDING' | 'DISPATCHED' | 'DELIVERED' | 'SIGNED' | 'INVOICED' | 'VOID';
export type InvoiceBadgeStatus =
  | 'DRAFT'
  | 'PENDING_ARCA'
  | 'ISSUED'
  | 'REJECTED'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'OVERDUE';
export type UserBadgeStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';

export type DomainColor = 'success' | 'warning' | 'danger' | 'neutral';

/**
 * packages/ui does not import Customer/Contract/Menu status types from
 * apps/web-admin — the monorepo dependency direction runs the other way.
 * These unions mirror the real backend values by hand (same criterion
 * contracts-module's design.md §10.4 already established for its Zod
 * schemas).
 */
export const CUSTOMER_DOMAIN_STYLES: Record<
  CustomerBadgeStatus,
  { color: DomainColor; label: string }
> = {
  ACTIVE: { color: 'success', label: 'Activa' },
  INACTIVE: { color: 'neutral', label: 'Inactiva' },
  SUSPENDED: { color: 'danger', label: 'Suspendida' },
};

export const CONTRACT_DOMAIN_STYLES: Record<
  ContractBadgeStatus,
  { color: DomainColor; label: string }
> = {
  DRAFT: { color: 'neutral', label: 'Borrador' },
  ACTIVE: { color: 'success', label: 'Activo' },
  PAUSED: { color: 'warning', label: 'Pausado' },
  EXPIRED: { color: 'neutral', label: 'Vencido' },
  TERMINATED: { color: 'danger', label: 'Dado de baja' },
};

/** INACTIVE (amber) is the third real menu state menu-table.tsx already
 * distinguishes for isActive=false without archiving — the spec's "as a
 * minimum" two states leaves room for it, it does not forbid it. */
export const MENU_DOMAIN_STYLES: Record<MenuBadgeStatus, { color: DomainColor; label: string }> = {
  ACTIVE: { color: 'success', label: 'Activo' },
  INACTIVE: { color: 'warning', label: 'Inactivo' },
  ARCHIVED: { color: 'neutral', label: 'Archivado' },
};

export const CALENDAR_DOMAIN_STYLES: Record<
  CalendarBadgeStatus,
  { color: DomainColor; label: string }
> = {
  PUBLISHED: { color: 'success', label: 'Publicado' },
  DRAFT: { color: 'neutral', label: 'Borrador' },
};

export const HOLIDAY_DOMAIN_STYLES: Record<
  HolidayBadgeStatus,
  { color: DomainColor; label: string }
> = {
  HAS_SERVICE: { color: 'success', label: 'Con servicio' },
  NO_SERVICE: { color: 'neutral', label: 'Sin servicio' },
};

/** `orders-module/design.md` §8.3: the four `Order.status` values, `SUBMITTED` in amber since it is still awaiting the cutoff close, not yet a final state. */
export const ORDER_DOMAIN_STYLES: Record<OrderBadgeStatus, { color: DomainColor; label: string }> =
  {
    DRAFT: { color: 'neutral', label: 'Borrador' },
    SUBMITTED: { color: 'warning', label: 'Enviado' },
    CONFIRMED: { color: 'success', label: 'Confirmado' },
    CANCELLED: { color: 'danger', label: 'Cancelado' },
  };

/**
 * `production-module/design.md` §8.3: labels match `apps/api`'s own CSV
 * export status labels (`build-production-csv.ts`'s `STATUS_LABELS`) so
 * the badge and the exported file never disagree on what to call the
 * same state.
 */
export const PRODUCTION_DOMAIN_STYLES: Record<
  ProductionBadgeStatus,
  { color: DomainColor; label: string }
> = {
  PROVISIONAL: { color: 'warning', label: 'Provisoria' },
  CLOSED: { color: 'success', label: 'Cerrada' },
};

/** `delivery-notes-module/design.md` §9.3: the six `DeliveryNote.status` values. */
export const DELIVERY_NOTE_DOMAIN_STYLES: Record<
  DeliveryNoteBadgeStatus,
  { color: DomainColor; label: string }
> = {
  PENDING: { color: 'neutral', label: 'Pendiente' },
  DISPATCHED: { color: 'warning', label: 'Despachado' },
  DELIVERED: { color: 'warning', label: 'Entregado' },
  SIGNED: { color: 'success', label: 'Firmado' },
  INVOICED: { color: 'success', label: 'Facturado' },
  VOID: { color: 'danger', label: 'Anulado' },
};

/**
 * `billing-module/design.md` §9.5: the four `Invoice.status` values this
 * change produces. `PARTIALLY_PAID`/`PAID`/`OVERDUE`/`VOIDED` do not exist
 * on `InvoiceStatus` yet (reserved for later changes), so they have no
 * entry here, same incremental criterion `DELIVERY_NOTE_DOMAIN_STYLES`
 * already followed.
 */
export const INVOICE_DOMAIN_STYLES: Record<
  InvoiceBadgeStatus,
  { color: DomainColor; label: string }
> = {
  DRAFT: { color: 'neutral', label: 'Borrador' },
  PENDING_ARCA: { color: 'warning', label: 'Enviada a ARCA' },
  ISSUED: { color: 'success', label: 'Emitida' },
  REJECTED: { color: 'danger', label: 'Rechazada' },
  // Collections (customer-account): the pending balance moves an issued invoice.
  PARTIALLY_PAID: { color: 'warning', label: 'Pago parcial' },
  PAID: { color: 'success', label: 'Pagada' },
  OVERDUE: { color: 'danger', label: 'Vencida' },
};

/** `internal-users/spec.md`: the four `User.status` values for internal staff. */
export const USER_DOMAIN_STYLES: Record<UserBadgeStatus, { color: DomainColor; label: string }> = {
  INVITED: { color: 'neutral', label: 'Invitado' },
  ACTIVE: { color: 'success', label: 'Activo' },
  SUSPENDED: { color: 'warning', label: 'Suspendido' },
  INACTIVE: { color: 'danger', label: 'Inactivo' },
};

/**
 * Tinted fill, dark text and a hairline ring rather than white on a solid
 * fill: white on emerald-500 or amber-500 sits near 2.5:1 and fails WCAG AA,
 * while the -800 text on a -50 fill clears 7:1 for every tone.
 */

/**
 * The real, exhaustive set of keys per domain — derived from the same
 * `*_DOMAIN_STYLES` maps `StatusBadge` itself renders from, so a caller
 * validating its own data against "is this a real status for this domain"
 * can never drift out of sync with what the badge actually knows how to
 * colour.
 */
export const STATUS_BADGE_DOMAIN_VALUES = {
  customer: Object.keys(CUSTOMER_DOMAIN_STYLES) as CustomerBadgeStatus[],
  contract: Object.keys(CONTRACT_DOMAIN_STYLES) as ContractBadgeStatus[],
  menu: Object.keys(MENU_DOMAIN_STYLES) as MenuBadgeStatus[],
  calendar: Object.keys(CALENDAR_DOMAIN_STYLES) as CalendarBadgeStatus[],
  holiday: Object.keys(HOLIDAY_DOMAIN_STYLES) as HolidayBadgeStatus[],
  order: Object.keys(ORDER_DOMAIN_STYLES) as OrderBadgeStatus[],
  production: Object.keys(PRODUCTION_DOMAIN_STYLES) as ProductionBadgeStatus[],
  deliverynote: Object.keys(DELIVERY_NOTE_DOMAIN_STYLES) as DeliveryNoteBadgeStatus[],
  invoice: Object.keys(INVOICE_DOMAIN_STYLES) as InvoiceBadgeStatus[],
  user: Object.keys(USER_DOMAIN_STYLES) as UserBadgeStatus[],
} as const;

export type StatusBadgeDomain = keyof typeof STATUS_BADGE_DOMAIN_VALUES;
